import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs, { Dayjs } from 'dayjs';
import { Box, ClosableDrawer, Flex, PrimaryPaper } from '@atoms/layout';
import { Accordion, AccordionDetails, AccordionSummary, Chip, Stack, styled, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Unstable_Grid2';
import { Button } from '@atoms/buttons';
import { v4 as uuidV4 } from 'uuid';
import { DateHelper } from '@bryntum/schedulerpro';
import { useForm, useFormContext } from 'react-hook-form';
import { Form } from '@molecules/form/form';
import {
  FormBlock,
  FormCheckbox,
  FormDatePicker,
  FormSelect,
  FormTextField,
  FormTimePicker,
} from '@molecules/form';
import { useNotification } from '@libs/snackbar';
import { parseError } from '@libs/api/errors';
import { AgencyUpdateData } from '@libs/models/agencies';
import { ContactInfo } from '@libs/models/common/contactInfo';
import { Place } from '@libs/models/common';
import { useAgencyEditStore } from '@libs/store/agencies/agencyEditDrawerStore';

type EditShiftDrawerProps = {
  onSaveCall?: () => void;
  localizationSubset: string;
};


export function EditAgencyDrawer({ onSaveCall, localizationSubset }: EditShiftDrawerProps) {
  const intl = useIntl();
  const { showSuccessIntl, showError } = useNotification();

  const {
    drawerOpen,
    loading: entityLoading,
    drawerMode,
    closeDrawer,
    addAgency,
    updateAgency,
    currentEntityId: currentId
  } = useAgencyEditStore();

  const isAddDialog = drawerMode == 'add';
  localizationSubset = isAddDialog ? `${localizationSubset}.add` : `${localizationSubset}.edit`;
  const open = drawerOpen;

  const loading = entityLoading;

  const methods = useForm<AgencyFormData>({
    defaultValues: {
      name: '',
      domainName: '',
      agencyType: '',
      linesOfBusiness: [],
      qualifications: [],
      contactFirstName: '',
      contactLastName: '',
      contactPhoneNumber: '',
      addressAddressFirst: '',
      addressAddressSecond: '',
      addressCity: '',
      addressState: '',
      addressZipCode: '',
    },
  });
  const { setValue, setError, watch } = methods;

  const handleClose = () => {
    closeDrawer();
    methods.reset();
  };
  const handleSubmit = async (isPublish: boolean, data: AgencyFormData) => {
    const sendingData: AgencyUpdateData = {
      id: '',
      name: data.name,
      domainName: data.domainName,
      agencyType: data.agencyType,
      linesOfBusiness: data.linesOfBusiness,
      qualifications: data.qualifications,
      contact: {
        email: data.contactEmail,
        firstName: data.contactFirstName,
        phoneNumber: data.contactPhoneNumber,
        lastName: data.contactLastName,
      },
      address: {
        addressFirst: data.addressAddressFirst,
        addressSecond: data.addressAddressSecond,
        city: data.addressCity,
        state: data.addressState,
        zipCode: data.addressZipCode,
      },
    };

    try {
      if (isAddDialog) {
        await addAgency(sendingData);
      } else {
        await updateAgency(currentId, sendingData);
      }

      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      }
    }
  };

  const handleAddActivate = async () => {};
  const handleAdd = async () => {};

  return (
    <ClosableDrawer
      open={open}
      loading={loading}
      onClose={handleClose}
      controls={
        <Stack direction={'row-reverse'} spacing={2} sx={(theme) => ({ paddingBottom: theme.spacing(1) })}>
          <MenuButton variant="text" onClick={handleAddActivate} disabled={loading}>
            <FormattedMessage id={`${localizationSubset}.menuButtons.addActivate.label`} />
          </MenuButton>
          <MenuButton variant="text" color={'error'} onClick={handleAdd} disabled={loading}>
            <FormattedMessage id={`${localizationSubset}.menuButtons.add.label`} />
          </MenuButton>
          <Box flex={1} />
          <MenuButton
            onClick={handleClose}
            sx={(theme) => ({ color: theme.palette.text.secondary })}
            disabled={loading}
          >
            <FormattedMessage id={`${localizationSubset}.menuButtons.back.label`} />
          </MenuButton>
        </Stack>
      }
    >
      <Stack direction={'column'} sx={{ height: '100%' }} spacing={2} width={{ xs: '300px', sm: '600px' }}>
        {/* Header */}
        <Stack direction={'row'}>
          <Typography variant="h5">
            <FormattedMessage id={`${localizationSubset}.dialogHeading`} />
          </Typography>
          <Box flex={1} />
        </Stack>
        <Form formUtils={methods} style={{ height: '100%' }} onSubmit={handleSubmit.bind(handleSubmit, false)}>
          {/* Basics */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.details.sectTitle`} />}>
            <Stack direction={'row'} spacing={2}>
              <FormTextField
                label={<FormattedMessage id={`${localizationSubset}.details.agencyName.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.details.agencyName.placeholder`,
                })}
                fullWidth
                name="agencyName"
              />
              <FormTextField
                label={<FormattedMessage id={`${localizationSubset}.details.domainName.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.details.domainName.placeholder`,
                })}
                fullWidth
                name="domainName"
              />
            </Stack>
            <Stack direction={'row'} spacing={2}>
              <FormTextField
                label={<FormattedMessage id={`${localizationSubset}.details.agencyType.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.details.agencyType.placeholder`,
                })}
                fullWidth
                name="agencyType"
              />
              <FormTextField
                label={<FormattedMessage id={`${localizationSubset}.details.linesOfBusiness.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.details.linesOfBusiness.placeholder`,
                })}
                fullWidth
                name="linesOfBusiness"
              />
            </Stack>
          </FormBlock>
          {/* Contact */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.contact.sectTitle`} />}>
            <Stack direction={'row'} spacing={2}>
              <FormTextField
                disabled
                label={<FormattedMessage id={`${localizationSubset}.contact.firstName.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.firstName.placeholder`,
                })}
                fullWidth
                name="firstName"
              />
              <FormTextField
                disabled
                label={<FormattedMessage id={`${localizationSubset}.contact.lastName.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.lastName.placeholder`,
                })}
                fullWidth
                name="lastName"
              />
            </Stack>
            <Stack direction={'row'} spacing={2}>
              <FormTextField
                disabled
                label={<FormattedMessage id={`${localizationSubset}.contact.email.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.email.placeholder`,
                })}
                fullWidth
                name="email"
              />
              <FormTextField
                disabled
                label={<FormattedMessage id={`${localizationSubset}.contact.phoneNumber.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.phoneNumber.placeholder`,
                })}
                fullWidth
                name="phoneNumber"
              />
            </Stack>
          </FormBlock>

          {/* Address */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.address.sectTitle`} />}>
            <Stack direction={'column'} spacing={1}>
            <Stack direction={'row'} spacing={2}>
              <FormTextField
                disabled
                label={<FormattedMessage id={`${localizationSubset}.contact.firstName.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.firstName.placeholder`,
                })}
                fullWidth
                name="firstName"
              />
              <FormTextField
                disabled
                label={<FormattedMessage id={`${localizationSubset}.contact.lastName.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.lastName.placeholder`,
                })}
                fullWidth
                name="lastName"
              />
            </Stack>
            <Stack direction={'row'} spacing={2}>
              <FormTextField
                disabled
                label={<FormattedMessage id={`${localizationSubset}.contact.email.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.email.placeholder`,
                })}
                fullWidth
                name="email"
              />
              <FormTextField
                disabled
                label={<FormattedMessage id={`${localizationSubset}.contact.phoneNumber.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.phoneNumber.placeholder`,
                })}
                fullWidth
                name="phoneNumber"
              />
            </Stack>
            </Stack>
          </FormBlock>
        </Form>
      </Stack>
    </ClosableDrawer>
  );
}
const MenuButton = styled(Button)(({ theme }) => ({
  // minWidth: theme.spacing(16.75),
}));

type AgencyFormData = {
  domainName: AgencyUpdateData['domainName'];
  qualifications: AgencyUpdateData['qualifications'];
  name: AgencyUpdateData['name'];
  agencyType: AgencyUpdateData['agencyType'];
  linesOfBusiness: AgencyUpdateData['linesOfBusiness'];
  contactFirstName: ContactInfo['firstName'];
  contactLastName: ContactInfo['lastName'];
  contactPhoneNumber: ContactInfo['phoneNumber'];
  contactEmail: ContactInfo['email'];
  addressAddressFirst: Place['addressFirst'];
  addressAddressSecond: Place['addressSecond'];
  addressCity: Place['city'];
  addressState: Place['state'];
  addressZipCode: Place['zipCode'];
};
